import { getErrorMessage } from '~/utils/errors'

/** Session: el usuario llegó al checkout y salió sin completar compra (recuperación de carrito). */
export const SESSION_CHECKOUT_PAGE_VISITED = 'byhours_checkout_page_visited'

export const useCheckoutStore = defineStore('checkout', () => {
  // Estado reactivo para los datos del checkout
  const checkoutData = ref<CheckoutData | null>(null)
  
  // Estado para indicar si los datos están listos
  const isDataReady = ref(false)
  
  // Función para establecer los datos del checkout
  const setCheckoutData = (data: CheckoutData) => {
    checkoutData.value = data
    isDataReady.value = true
    
    // Persistir temporalmente en sessionStorage para mantener datos durante navegación
    if (import.meta.client) {
      sessionStorage.setItem('checkout_data', JSON.stringify(data))
      // Nueva selección: aún no ha “abandonado” el checkout de esta reserva
      sessionStorage.removeItem('byhours_checkout_recovery_dismissed')
      sessionStorage.removeItem(SESSION_CHECKOUT_PAGE_VISITED)
    }
  }
  
  // Función para obtener los datos del checkout
  const getCheckoutData = (): CheckoutData | null => {
    // Si no hay datos en memoria, intentar recuperar de sessionStorage
    if (!checkoutData.value && import.meta.client) {
      const storedData = sessionStorage.getItem('checkout_data')
      if (storedData) {
        try {
          const parsedData = JSON.parse(storedData)
          checkoutData.value = parsedData
          isDataReady.value = true
        } catch (error: unknown) {
          console.error('Error parsing stored checkout data:', getErrorMessage(error))
          clearCheckoutData()
        }
      }
    }
    
    return checkoutData.value
  }
  
  // Función para limpiar los datos del checkout
  const clearCheckoutData = () => {
    checkoutData.value = null
    isDataReady.value = false
    
    if (import.meta.client) {
      sessionStorage.removeItem('checkout_data')
      sessionStorage.removeItem(SESSION_CHECKOUT_PAGE_VISITED)
    }
  }
  
  // Función para verificar si hay datos válidos
  const hasValidData = (): boolean => {
    const data = getCheckoutData()
    if (data === null || data.totalAmount == null) return false
    const hotelId = data.hotelId ?? data.hotelData?.id
    const roomId = data.roomId ?? data.roomData?.id
    return !!(hotelId && roomId)
  }
  
  // Función para actualizar un campo específico
  const updateCheckoutField = <K extends keyof CheckoutData>(field: K, value: CheckoutData[K]) => {
    if (checkoutData.value) {
      checkoutData.value[field] = value
      
      // Actualizar también en sessionStorage
      if (import.meta.client) {
        sessionStorage.setItem('checkout_data', JSON.stringify(checkoutData.value))
      }
    }
  }
  
  return {
    checkoutData,
    isDataReady,
    setCheckoutData,
    getCheckoutData,
    clearCheckoutData,
    hasValidData,
    updateCheckoutField
  }
}) 